import React, { useEffect, useRef, useState } from 'react';
import { MessageSquare, Send, Bot, User as UserIcon, Radio } from 'lucide-react';
import pipelineService from '../services/pipelineService';
import { Spinner } from './ui';

type Msg = { role: 'user' | 'assistant'; content: string };
type AgentKey = 'jira' | 'marketing' | 'doc';

const PRESET: Record<AgentKey, { title: string; blurb: string; suggestions: string[] }> = {
  jira: {
    title: 'Ask the Jira agent',
    blurb: 'Ask about your tickets — priorities, what’s late, who’s overloaded. I answer from the real tickets and never change anything.',
    suggestions: ['What’s overdue or at risk?', 'Summarise the highest-priority tickets.', 'Who has the most open tickets?', 'What should I tackle first?'],
  },
  marketing: {
    title: 'Ask the Marketing agent',
    blurb: 'Riff on angles, headlines and edits for your content. I work from your brief and current draft — you approve the final copy.',
    suggestions: ['Punch up the opening line.', 'Give me 3 alternative headlines.', 'Make it shorter and less salesy.', 'Does this fit the channel?'],
  },
  doc: {
    title: 'Ask about this document',
    blurb: 'Ask about the extracted terms — dates, parties, obligations, risks. I answer only from what was extracted and never invent terms.',
    suggestions: ['What’s the termination notice period?', 'Flag any unusual or risky clauses.', 'Who are the parties and key dates?', 'Summarise the obligations.'],
  },
};

const sourceLabel = (agentKey: AgentKey, source?: string, items?: number): string => {
  const n = items ?? 0;
  if (source === 'run') {
    if (agentKey === 'jira') return `Grounded on this run · ${n} ticket${n === 1 ? '' : 's'}`;
    if (agentKey === 'marketing') return 'Grounded on your brief & draft';
    return `Grounded on ${n} extracted term${n === 1 ? '' : 's'}`;
  }
  if (source === 'live') return `Grounded on ${n} live ticket${n === 1 ? '' : 's'}`;
  if (source === 'config') return 'Read-only copy assistant';
  if (source === 'unconfigured') return 'Jira not connected';
  if (source === 'none') return 'Open a document to ground answers';
  if (source === 'error') return 'Couldn’t read context';
  return 'Read-only assistant';
};

/**
 * Grounded, read-only chat with an agent. Talks to /pipeline/{agent}/chat, which answers from the
 * current run's context (when runId is set) or the agent's live working context. It explains and
 * reasons only — any real change still goes through that agent's review gate.
 */
const AgentChatPanel: React.FC<{ agentKey: AgentKey; runId?: string }> = ({ agentKey, runId }) => {
  const preset = PRESET[agentKey];
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [grounded, setGrounded] = useState<{ source: string; items: number } | null>(null);
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [messages, busy]);

  const send = async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;
    const next: Msg[] = [...messages, { role: 'user', content: q }];
    setMessages(next);
    setInput('');
    setBusy(true);
    try {
      const res = await pipelineService.agentChat(agentKey, next, runId);
      setGrounded(res.grounded);
      setMessages((m) => [...m, { role: 'assistant', content: res.reply }]);
    } catch (e: any) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: e?.response?.data?.detail || 'Sorry — I couldn’t reach the agent just now.',
      }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-card" style={{ display: 'flex', flexDirection: 'column', maxHeight: 460 }}>
      <div className="mx-card-head" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 className="mx-card-title" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <MessageSquare size={15} /> {preset.title}
        </h3>
        <span className="mx-chip" style={{ marginLeft: 'auto', fontSize: 11 }}>
          <Radio size={11} className={grounded?.source === 'live' ? 'mx-pulsing' : undefined} />
          {sourceLabel(agentKey, grounded?.source, grounded?.items)}
        </span>
      </div>

      <div ref={scroller} className="mx-card-pad"
        style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, minHeight: 160 }}>
        {messages.length === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="mx-muted" style={{ fontSize: 12.5 }}>{preset.blurb}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {preset.suggestions.map((s) => (
                <button key={s} className="mx-chip" style={{ cursor: 'pointer', fontSize: 11.5 }}
                  onClick={() => send(s)}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start',
            flexDirection: m.role === 'user' ? 'row-reverse' : 'row' }}>
            <span style={{ display: 'grid', placeItems: 'center', width: 24, height: 24, flex: '0 0 auto',
              borderRadius: 6, background: m.role === 'user' ? 'var(--mx-surface-2)' : 'var(--mx-accent)',
              color: m.role === 'user' ? 'var(--mx-text)' : '#fff' }}>
              {m.role === 'user' ? <UserIcon size={13} /> : <Bot size={13} />}
            </span>
            <div className="mx-card-pad" style={{
              padding: '8px 11px', borderRadius: 9, maxWidth: '86%', fontSize: 13, lineHeight: 1.5,
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              background: m.role === 'user' ? 'var(--mx-surface-2)' : 'var(--mx-surface)',
              border: '1px solid var(--mx-border)' }}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ display: 'grid', placeItems: 'center', width: 24, height: 24,
              borderRadius: 6, background: 'var(--mx-accent)', color: '#fff' }}><Bot size={13} /></span>
            <Spinner label="Thinking…" />
          </div>
        )}
      </div>

      <form className="mx-card-pad" style={{ borderTop: '1px solid var(--mx-border)', display: 'flex', gap: 8 }}
        onSubmit={(e) => { e.preventDefault(); send(input); }}>
        <input className="mx-input" placeholder="Ask a question…" value={input}
          disabled={busy} onChange={(e) => setInput(e.target.value)} style={{ flex: 1 }} />
        <button type="submit" className="mx-btn mx-btn-primary" disabled={busy || !input.trim()}
          style={{ display: 'grid', placeItems: 'center', width: 38, padding: 0 }} aria-label="Send">
          <Send size={15} />
        </button>
      </form>
    </div>
  );
};

export default AgentChatPanel;
